import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button, Dropdown, Header, Icon, Image, Input, Label, Message,
  Modal, Segment, TextArea,
} from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { TournamentDto, TournamentMatchDto } from '../../app/models/Tournament';

interface Props {
  match: TournamentMatchDto;
  tournament: TournamentDto;
  onClose: () => void;
}

export default observer(function TournamentMatchModal({ match, tournament, onClose }: Props) {
  const { tournamentStore } = useStore();
  const { t } = useTranslation();

  const result = match.result;
  const canAdmin = match.isCurrentUserOrganizer;
  const canReportResult = match.isCurrentUserCaptainOfTeamA || match.isCurrentUserCaptainOfTeamB || match.isCurrentUserReferee || canAdmin;

  // Initialize score fields with existing result values if present
  const [scoreA, setScoreA] = useState(result?.scoreA?.toString() ?? '');
  const [scoreB, setScoreB] = useState(result?.scoreB?.toString() ?? '');
  const [disputeReason, setDisputeReason] = useState('');
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [resolveScoreA, setResolveScoreA] = useState('');
  const [resolveScoreB, setResolveScoreB] = useState('');
  const [resolveNote, setResolveNote] = useState('');
  const [showWalkoverForm, setShowWalkoverForm] = useState(false);
  const [walkoverWinnerId, setWalkoverWinnerId] = useState('');
  const [walkoverReason, setWalkoverReason] = useState('');
  const [showScheduleEdit, setShowScheduleEdit] = useState(false);

  // Convert a UTC ISO string to the "YYYY-MM-DDTHH:mm" format that
  // datetime-local expects (always in the user's local timezone).
  const utcToLocalInput = (utcStr: string | undefined): string => {
    if (!utcStr) return '';
    const d = new Date(utcStr);
    // Format as local time: pad each part manually to avoid locale differences
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [scheduledAt, setScheduledAt] = useState(() => utcToLocalInput(match.scheduledAt));

  // Sync scheduledAt field whenever the match prop is refreshed from the store
  // (e.g. after a successful save, the parent passes the updated match object)
  React.useEffect(() => {
    setScheduledAt(utcToLocalInput(match.scheduledAt));
  }, [match.scheduledAt]);

  // Sync score fields when match result changes (e.g. after store refresh)
  React.useEffect(() => {
    setScoreA(match.result?.scoreA?.toString() ?? '');
    setScoreB(match.result?.scoreB?.toString() ?? '');
  }, [match.result?.scoreA, match.result?.scoreB]);

  const statusColors: Record<string, string> = {
    Scheduled: 'blue', Reported: 'orange', Confirmed: 'green',
    Disputed: 'red', Resolved: 'purple', Walkover: 'grey',
  };

  const walkoverOptions = [
    ...(match.teamAId ? [{ key: match.teamAId, value: match.teamAId, text: match.teamAName }] : []),
    ...(match.teamBId ? [{ key: match.teamBId, value: match.teamBId, text: match.teamBName }] : []),
  ];

  const handleReport = async () => {
    if (scoreA === '' || scoreB === '') return;
    await tournamentStore.reportResult(match.id, Number(scoreA), Number(scoreB));
    onClose();
  };

  const handleUpdate = async () => {
    if (scoreA === '' || scoreB === '') return;
    await tournamentStore.updateMatchResult(match.id, Number(scoreA), Number(scoreB));
    onClose();
  };

  const handleConfirm = async () => {
    await tournamentStore.confirmResult(match.id);
    onClose();
  };

  const handleDispute = async () => {
    if (!disputeReason.trim()) return;
    await tournamentStore.disputeResult(match.id, disputeReason);
    onClose();
  };

  const handleResolve = async () => {
    if (resolveScoreA === '' || resolveScoreB === '' || !resolveNote.trim()) return;
    await tournamentStore.resolveDispute(match.id, Number(resolveScoreA), Number(resolveScoreB), resolveNote);
    onClose();
  };

  const handleWalkover = async () => {
    if (!walkoverWinnerId || !walkoverReason.trim()) return;
    await tournamentStore.declareWalkover(match.id, walkoverWinnerId, walkoverReason);
    onClose();
  };

  const handleScheduleUpdate = async () => {
    if (!scheduledAt) return;
    // datetime-local gives us a local-time string without timezone.
    // new Date() parses it as LOCAL time and toISOString() converts to UTC — correct.
    const success = await tournamentStore.updateMatchSchedule(match.id, new Date(scheduledAt));
    if (success) setShowScheduleEdit(false);
  };

  return (
    <Modal open onClose={onClose} size="small" closeIcon>
      <Modal.Header>
        <Icon name="shield alternate" color="teal" />
        {t('tournament.matchModal.title')} — {t('tournament.bracket.round')} {match.round}, #{match.matchNumber}
      </Modal.Header>

      <Modal.Content>
        {/* VS Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
          {/* Team A */}
          <div style={{ textAlign: 'center', flex: '1 1 120px' }}>
            <Image
              src={match.teamALogoUrl || '/assets/team-placeholder.png'}
              circular centered
              style={{ width: 52, height: 52, objectFit: 'cover', margin: '0 auto 6px' }}
            />
            <div style={{ fontWeight: 700, fontSize: '0.95em' }}>{match.teamAName || 'BYE'}</div>
          </div>

          {/* Score / VS */}
          <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
            {result ? (
              <div style={{ fontSize: '2em', fontWeight: 900 }}>
                <span style={{ color: result.scoreA > result.scoreB ? '#21ba45' : '#333' }}>{result.scoreA}</span>
                <span style={{ color: '#ccc', margin: '0 8px' }}>:</span>
                <span style={{ color: result.scoreB > result.scoreA ? '#21ba45' : '#333' }}>{result.scoreB}</span>
              </div>
            ) : (
              <div style={{ fontSize: '1.4em', fontWeight: 900, color: '#ccc' }}>VS</div>
            )}
            <Label size="mini" color={statusColors[match.status] as any} style={{ marginTop: 6 }}>
              {t(`tournament.matchStatus.${match.status.toLowerCase()}`)}
            </Label>
            {match.scheduledAt && (
              <div style={{ fontSize: '0.75em', color: '#888', marginTop: 6 }}>
                <Icon name="calendar" />
                {format(new Date(match.scheduledAt), 'dd MMM yyyy, HH:mm')}
              </div>
            )}
          </div>

          {/* Team B */}
          <div style={{ textAlign: 'center', flex: '1 1 120px' }}>
            {match.teamBName ? (
              <>
                <Image
                  src={match.teamBLogoUrl || '/assets/team-placeholder.png'}
                  circular centered
                  style={{ width: 52, height: 52, objectFit: 'cover', margin: '0 auto 6px' }}
                />
                <div style={{ fontWeight: 700, fontSize: '0.95em' }}>{match.teamBName}</div>
              </>
            ) : (
              <div style={{ color: '#bbb', textAlign: 'center', paddingTop: 10 }}>
                <Icon name="question circle" size="large" />
                <div style={{ fontSize: '0.8em' }}>TBD</div>
              </div>
            )}
          </div>
        </div>

        {/* Referees */}
        {match.referees?.length > 0 && (
          <Segment secondary style={{ marginBottom: 14 }}>
            <div style={{ fontSize: '0.82em', color: '#666' }}>
              <Icon name="gavel" />
              {t('tournament.matchModal.referees')}:{' '}
              {match.referees.map(r => r.fullName).join(', ')}
            </div>
          </Segment>
        )}

        {/* Walkover notice */}
        {match.status === 'Walkover' && (
          <Message warning>
            <Icon name="exclamation triangle" />
            {t('tournament.matchModal.walkoverDeclared')}: {match.walkoverReason}
          </Message>
        )}

        {/* Reported by referee indicator */}
        {result?.reporterRole === 'Referee' && (
          <Message size="small" info>
            <Icon name="gavel" />
            {t('tournament.matchModal.reportedByReferee')}: {result.reportedByFullName}
          </Message>
        )}

        {/* Dispute reason */}
        {result?.disputeReason && (
          <Message warning>
            <Icon name="exclamation triangle" />
            <strong>{t('tournament.matchModal.disputeReason')}: </strong>{result.disputeReason}
          </Message>
        )}

        {/* Resolution note */}
        {result?.resolutionNote && (
          <Message info>
            <Icon name="gavel" />
            <strong>{t('tournament.matchModal.resolution')}: </strong>{result.resolutionNote}
          </Message>
        )}

        {/* ── Report Result — prikazuje se samo kada rezultat ne postoji ────── */}
        {!result && canReportResult && match.teamAId && match.teamBId && (
          <Segment>
            <Header as="h5">{t('tournament.matchModal.reportResult')}</Header>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.8em', color: '#666', marginBottom: 4 }}>{match.teamAName}</div>
                <Input
                  type="number" min={0}
                  value={scoreA}
                  onChange={e => setScoreA(e.target.value)}
                  style={{ width: 72 }}
                />
              </div>
              <div style={{ fontWeight: 900, fontSize: '1.3em', color: '#bbb', alignSelf: 'flex-end', marginBottom: 4 }}>:</div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.8em', color: '#666', marginBottom: 4 }}>{match.teamBName}</div>
                <Input
                  type="number" min={0}
                  value={scoreB}
                  onChange={e => setScoreB(e.target.value)}
                  style={{ width: 72 }}
                />
              </div>
              <Button
                positive size="small"
                loading={tournamentStore.submitting}
                disabled={scoreA === '' || scoreB === '' || tournamentStore.submitting}
                onClick={handleReport}
              >
                <Icon name="send" />{t('tournament.matchModal.report')}
              </Button>
            </div>
            {match.isCurrentUserReferee && (
              <div style={{ fontSize: '0.75em', color: '#888', marginTop: 6 }}>
                <Icon name="gavel" size="small" />
                {t('tournament.matchModal.reportingAsReferee')}
              </div>
            )}
          </Segment>
        )}

        {/* ── Admin: Edit Result — samo kada je status Reported ─────────────── */}
       {canAdmin && (result?.status === 'Reported' || result?.status === 'Confirmed') && match.teamAId && match.teamBId && (
          <Segment color="yellow">
            <Header as="h5">
              <Icon name="edit" color="yellow" />
              {t('tournament.matchModal.editResult')}
            </Header>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.8em', color: '#666', marginBottom: 4 }}>{match.teamAName}</div>
                <Input
                  type="number" min={0}
                  value={scoreA}
                  onChange={e => setScoreA(e.target.value)}
                  style={{ width: 72 }}
                />
              </div>
              <div style={{ fontWeight: 900, fontSize: '1.3em', color: '#bbb', alignSelf: 'flex-end', marginBottom: 4 }}>:</div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.8em', color: '#666', marginBottom: 4 }}>{match.teamBName}</div>
                <Input
                  type="number" min={0}
                  value={scoreB}
                  onChange={e => setScoreB(e.target.value)}
                  style={{ width: 72 }}
                />
              </div>
              <Button
                color="yellow" size="small"
                loading={tournamentStore.submitting}
                disabled={scoreA === '' || scoreB === '' || tournamentStore.submitting}
                onClick={handleUpdate}
              >
                <Icon name="save" />{t('tournament.matchModal.updateResult')}
              </Button>
            </div>
            <div style={{ fontSize: '0.75em', color: '#888', marginTop: 6 }}>
              <Icon name="info circle" size="small" />
              {t('tournament.matchModal.editResultHint')}
            </div>
          </Segment>
        )}

        {/* ── Confirm / Dispute ─────────────────────────────────────────────── */}
        {result?.status === 'Reported' && (match.isCurrentUserCaptainOfTeamA || match.isCurrentUserCaptainOfTeamB || canAdmin) && (
          <Segment>
            <Header as="h5">{t('tournament.matchModal.confirmOrDispute')}</Header>
            {!showDisputeForm ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <Button positive size="small" loading={tournamentStore.submitting} onClick={handleConfirm}>
                  <Icon name="check" />{t('tournament.matchModal.confirm')}
                </Button>
                <Button negative basic size="small" onClick={() => setShowDisputeForm(true)}>
                  <Icon name="exclamation triangle" />{t('tournament.matchModal.dispute')}
                </Button>
              </div>
            ) : (
              <div>
                <TextArea
                  value={disputeReason}
                  onChange={(_, d) => setDisputeReason(d.value as string)}
                  placeholder={t('tournament.matchModal.disputeReasonPlaceholder')}
                  rows={3}
                  style={{ width: '100%', marginBottom: 8, borderRadius: 6, border: '1px solid #ddd', padding: 8 }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button negative size="small" loading={tournamentStore.submitting}
                    disabled={!disputeReason.trim()} onClick={handleDispute}>
                    <Icon name="send" />{t('tournament.matchModal.submitDispute')}
                  </Button>
                  <Button basic size="small" onClick={() => setShowDisputeForm(false)}>
                    {t('tournament.cancel')}
                  </Button>
                </div>
              </div>
            )}
          </Segment>
        )}

        {/* ── Organizer: Resolve Dispute ────────────────────────────────────── */}
        {result?.status === 'Disputed' && canAdmin && (
          <Segment color="red">
            <Header as="h5" color="red">
              <Icon name="gavel" />{t('tournament.matchModal.resolveDispute')}
            </Header>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.8em', color: '#666', marginBottom: 4 }}>{match.teamAName}</div>
                <Input type="number" min={0} value={resolveScoreA}
                  onChange={e => setResolveScoreA(e.target.value)} style={{ width: 72 }} />
              </div>
              <div style={{ fontWeight: 900, fontSize: '1.3em', color: '#bbb' }}>:</div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.8em', color: '#666', marginBottom: 4 }}>{match.teamBName}</div>
                <Input type="number" min={0} value={resolveScoreB}
                  onChange={e => setResolveScoreB(e.target.value)} style={{ width: 72 }} />
              </div>
            </div>
            <TextArea
              value={resolveNote}
              onChange={(_, d) => setResolveNote(d.value as string)}
              placeholder={t('tournament.matchModal.resolveNotePlaceholder')}
              rows={2}
              style={{ width: '100%', marginBottom: 8, borderRadius: 6, border: '1px solid #ddd', padding: 8 }}
            />
            <Button negative size="small" loading={tournamentStore.submitting}
              disabled={resolveScoreA === '' || resolveScoreB === '' || !resolveNote.trim()}
              onClick={handleResolve}>
              <Icon name="gavel" />{t('tournament.matchModal.resolve')}
            </Button>
          </Segment>
        )}

        {/* ── Organizer: Walkover ───────────────────────────────────────────── */}
        {canAdmin && match.status === 'Scheduled' && match.teamAId && match.teamBId && (
          <Segment secondary>
            {!showWalkoverForm ? (
              <Button basic size="mini" color="grey" onClick={() => setShowWalkoverForm(true)}>
                <Icon name="flag" />{t('tournament.matchModal.declareWalkover')}
              </Button>
            ) : (
              <div>
                <Header as="h5">{t('tournament.matchModal.walkoverTitle')}</Header>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: '0.88em' }}>
                    {t('tournament.matchModal.walkoverWinner')}
                  </label>
                  <Dropdown
                    selection fluid
                    options={walkoverOptions}
                    value={walkoverWinnerId}
                    onChange={(_, d) => setWalkoverWinnerId(d.value as string)}
                    placeholder={t('tournament.matchModal.walkoverWinnerPlaceholder')}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: '0.88em' }}>
                    {t('tournament.matchModal.walkoverReason')}
                  </label>
                  <Input fluid value={walkoverReason} onChange={e => setWalkoverReason(e.target.value)}
                    placeholder={t('tournament.matchModal.walkoverReasonPlaceholder')} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="small" color="grey" loading={tournamentStore.submitting}
                    disabled={!walkoverWinnerId || !walkoverReason.trim()} onClick={handleWalkover}>
                    <Icon name="flag" />{t('tournament.matchModal.confirmWalkover')}
                  </Button>
                  <Button basic size="small" onClick={() => setShowWalkoverForm(false)}>
                    {t('tournament.cancel')}
                  </Button>
                </div>
              </div>
            )}
          </Segment>
        )}

        {/* ── Organizer: Schedule edit ──────────────────────────────────────── */}
        {canAdmin && (
          <Segment secondary>
            {!showScheduleEdit ? (
              <Button basic size="mini" onClick={() => setShowScheduleEdit(true)}>
                <Icon name="calendar" />{t('tournament.matchModal.editSchedule')}
              </Button>
            ) : (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: '0.85em' }}>
                    {t('tournament.matchModal.scheduledAt')}
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={e => setScheduledAt(e.target.value)}
                    style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ddd' }}
                  />
                </div>
                <Button size="small" positive loading={tournamentStore.submitting}
                  onClick={handleScheduleUpdate}>
                  <Icon name="save" />{t('tournament.matchModal.saveSchedule')}
                </Button>
                <Button basic size="small" onClick={() => setShowScheduleEdit(false)}>
                  {t('tournament.cancel')}
                </Button>
              </div>
            )}
          </Segment>
        )}

        {/* ── Referee assignment (admin) ────────────────────────────────────── */}
        {canAdmin && tournament.hasReferees && (
          <RefereeAssignSection matchId={match.id} tournamentId={tournament.id} match={match} />
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button basic onClick={onClose}>
          <Icon name="close" />{t('tournament.cancel')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
});

// ── Referee assign sub-component ──────────────────────────────────────────────

const RefereeAssignSection = observer(function RefereeAssignSection({
  matchId, tournamentId, match,
}: { matchId: string; tournamentId: string; match: TournamentMatchDto }) {
  const { tournamentStore } = useStore();
  const { t } = useTranslation();
  const [selectedRef, setSelectedRef] = useState('');

  const confirmedRefs = tournamentStore.referees.filter(r => r.status === 'Confirmed');
  const refOptions = confirmedRefs.map(r => ({
    key: r.userId, value: r.userId, text: r.fullName,
    image: { avatar: true, src: r.imageUrl || '/assets/user.png' },
  }));
  const assignedIds = match.referees.map(r => r.userId);
  const availableOptions = refOptions.filter(o => !assignedIds.includes(o.value));

  return (
    <Segment secondary>
      <Header as="h6" color="grey">
        <Icon name="gavel" />{t('tournament.matchModal.assignReferee')}
      </Header>
      {match.referees.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          {match.referees.map(r => (
            <Label key={r.userId} size="small">
              <Icon name="gavel" />
              {r.fullName}
              <Label.Detail
                style={{ cursor: 'pointer', marginLeft: 6 }}
                onClick={() => tournamentStore.removeReferee(tournamentId, r.userId)}
              >✕</Label.Detail>
            </Label>
          ))}
        </div>
      )}
      {availableOptions.length > 0 && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Dropdown
            selection compact
            options={availableOptions}
            value={selectedRef}
            onChange={(_, d) => setSelectedRef(d.value as string)}
            placeholder={t('tournament.matchModal.selectReferee')}
          />
          <Button size="mini" primary
            disabled={!selectedRef || tournamentStore.submitting}
            loading={tournamentStore.submitting}
            onClick={() => { tournamentStore.assignRefereeToMatch(matchId, selectedRef); setSelectedRef(''); }}>
            <Icon name="plus" />{t('tournament.matchModal.assign')}
          </Button>
        </div>
      )}
      {availableOptions.length === 0 && confirmedRefs.length === 0 && (
        <div style={{ fontSize: '0.8em', color: '#aaa' }}>
          <Icon name="info circle" />{t('tournament.matchModal.noConfirmedReferees')}
        </div>
      )}
    </Segment>
  );
});